import React, { useState } from 'react';
import { MapPin, Phone, Mail, Instagram, Send, Clock, CheckCircle2 } from 'lucide-react';
import { engineerInfo, serviceAreas, faqItems } from '../../mock';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSubmitted) return;

    try {
      if (typeof window !== 'undefined') {
        if (typeof window.gtag_report_conversion === 'function') {
          window.gtag_report_conversion();
        } else if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17954212675/NNiICK2PtpAcEMOWnvFC'
          });
        }
      }
    } catch (error) {
      console.warn('Erro ao registrar conversão Google Ads:', error);
    }

    const whatsappMessage = `Olá! Meu nome é ${formData.name}.%0A%0ATelefone: ${formData.phone}%0AEmail: ${formData.email}%0ACidade: ${formData.city}%0A%0AMensagem: ${formData.message}`;
    
    window.open(`${engineerInfo.whatsappLink}?text=${whatsappMessage}`, '_blank');
    
    setIsSubmitted(true);
    toast.success('Redirecionando para o WhatsApp...');
    
    setTimeout(() => {
      setFormData({ name: '', phone: '', email: '', city: '', message: '' });
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <section id="contato" className="py-12 sm:py-16 bg-white">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#A69076] text-white mb-4 text-sm px-4 py-1 hover:scale-105 transition-transform">
              Entre em Contato
            </Badge>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#073A40] mb-4">
              Vamos Conversar Sobre Seu Projeto?
            </h2>
            <p className="text-lg sm:text-xl text-[#06323B] max-w-2xl mx-auto">
              Estou pronta para atender você. Entre em contato e receba seu orçamento personalizado.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
            <Card className="p-6 sm:p-8 bg-gradient-to-br from-[#073A40] to-[#06323B] border-2 border-[#A69076]/50 hover:border-[#A69076] transition-all shadow-lg">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                Informações de Contato
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#A69076] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#A69076] font-semibold mb-1">WhatsApp</p>
                    <a 
                      href={engineerInfo.whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base sm:text-lg text-white font-semibold hover:text-[#A69076] transition-colors"
                    >
                      {engineerInfo.whatsapp}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#A69076] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#A69076] font-semibold mb-1">E-mail</p>
                    <a 
                      href={`mailto:${engineerInfo.email}`}
                      className="text-sm sm:text-base text-white hover:text-[#A69076] transition-colors break-all"
                    >
                      {engineerInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#A69076] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Instagram className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#A69076] font-semibold mb-1">Instagram</p>
                    <a 
                      href={engineerInfo.instagramLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base sm:text-lg text-white font-semibold hover:text-[#A69076] transition-colors"
                    >
                      {engineerInfo.instagram}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#A69076] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#A69076] font-semibold mb-2">Áreas de Atendimento</p>
                    <div className="flex flex-wrap gap-2">
                      {serviceAreas.map((city, index) => (
                        <Badge 
                          key={index}
                          variant="outline"
                          className="border-[#A69076] text-white text-xs hover:bg-[#A69076] transition-colors"
                        >
                          {city}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4 pt-4 border-t border-[#A69076]/30">
                  <div className="w-12 h-12 bg-[#A69076] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#A69076] font-semibold mb-1">Horário de Atendimento</p>
                    <p className="text-sm text-white">
                      Segunda a Sexta: 8h às 18h<br />
                      Sábado: 8h às 12h
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6 sm:p-8 border-2 border-[#A69076] bg-white">
              <h3 className="text-2xl sm:text-3xl font-bold text-[#073A40] mb-6">
                Envie Sua Mensagem
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#073A40] mb-2">
                    Nome Completo *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Seu nome"
                    className="border-[#A69F97] focus:border-[#073A40]"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-[#073A40] mb-2">
                      Telefone/WhatsApp *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(84) 99999-9999"
                      className="border-[#A69F97] focus:border-[#073A40]"
                    />
                  </div>

                  <div>
                    <label htmlFor="city" className="block text-sm font-semibold text-[#073A40] mb-2">
                      Cidade *
                    </label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Sua cidade"
                      className="border-[#A69F97] focus:border-[#073A40]"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#073A40] mb-2">
                    E-mail
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="seu@email.com"
                    className="border-[#A69F97] focus:border-[#073A40]"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#073A40] mb-2">
                    Mensagem *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Conte-me sobre seu projeto ou necessidade..."
                    rows={4}
                    className="border-[#A69F97] focus:border-[#073A40] resize-none"
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  className="w-full bg-[#073A40] hover:bg-[#06323B] text-white font-semibold py-6 transition-all duration-300 hover:scale-105"
                  disabled={isSubmitted}
                >
                  {isSubmitted ? (
                    <>
                      <CheckCircle2 className="mr-2" size={20} />
                      Enviado!
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Enviar via WhatsApp
                    </>
                  )}
                </Button>
              </form>
            </Card>
          </div>

          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-bold text-[#073A40] text-center mb-8">
              Perguntas Frequentes
            </h3>
            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-[#F2F2F2] border-2 border-[#A69076]/30 rounded-lg px-6"
                >
                  <AccordionTrigger className="text-left text-base sm:text-lg font-semibold text-[#073A40] hover:text-[#A69076] hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-[#06323B] leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
